import { Provider, signal } from '@angular/core';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';

import { HOTEL_RAIL } from './hotel.routes';

export { HOTEL_RAIL } from './hotel.routes';

/* What the hotel store last counted, published here so the ROOT projection reads it without
   importing the store — which would pull the whole hotel into the app's initial chunk (the 7 MB
   budget, M2; the restaurant's, the salon's, the grocery's and invoicing's pattern). Until the hotel
   has been opened this session there is nothing counted, and no badge. Nothing here may import the
   store or the records (`hotel.routes.ts` is root-light too). */
const ARRIVALS = signal(0);
const TO_CLEAN = signal(0);
const DEPARTING_OWING = signal(0);
const AUDIT = signal(0);

/** What the store counted — the four badged rows. */
export interface HotelBadgeCounts {
  /** Stays arriving today (or late) — Bookings. */
  readonly arrivals: number;
  /** Rooms whose board row is not clean — Cleaning. */
  readonly toClean: number;
  /** Stays leaving today with money still on the folio — Folios. */
  readonly departingOwing: number;
  /** Tonight's desk work before the night can be posted: arrivals + departures + guests in house without ID — Audit. */
  readonly audit: number;
}

/** The store's one write. */
export function publishHotelBadges(c: HotelBadgeCounts): void {
  if (ARRIVALS() !== c.arrivals) ARRIVALS.set(c.arrivals);
  if (TO_CLEAN() !== c.toClean) TO_CLEAN.set(c.toClean);
  if (DEPARTING_OWING() !== c.departingOwing) DEPARTING_OWING.set(c.departingOwing);
  if (AUDIT() !== c.audit) AUDIT.set(c.audit);
}

/**
 * The hotel's rail badges (the DS `hotel.attention.ts`, D149/D158). Each rule carries the `capKey`
 * of the screen it points at, so a badge cannot advertise work behind a capability the tenant does
 * not have — absent-at-zero and absent-because-unregistered look identical from outside (D65).
 *
 * - **Bookings** — arrivals today;
 * - **Cleaning** — rooms not clean;
 * - **Folios** — departing AND still owing, not every open folio: nine rooms with a running bill is
 *   the hotel being full, not a queue anyone can clear (`ht-app.jsx:218`);
 * - **Audit** (kz2 M7) — what stands between the desk and posting the night: the arrivals and
 *   departures still to process, and any guest in house without an ID (the audit's stop, #140).
 */
export function hotelAttentionProjections(): readonly AttentionProjection[] {
  return [
    {
      rule: { id: 'hotel.bookings.arrivals', moduleId: 'hotel', capKey: 'bookings', railItemId: HOTEL_RAIL.bookings, enabled: true, scope: 'site' },
      count: ARRIVALS.asReadonly(),
    },
    {
      rule: { id: 'hotel.housekeeping.toclean', moduleId: 'hotel', capKey: 'housekeeping', railItemId: HOTEL_RAIL.housekeeping, enabled: true, scope: 'site' },
      count: TO_CLEAN.asReadonly(),
    },
    {
      rule: { id: 'hotel.folio.open', moduleId: 'hotel', capKey: 'folio', railItemId: HOTEL_RAIL.folios, enabled: true, scope: 'site' },
      count: DEPARTING_OWING.asReadonly(),
    },
    {
      rule: { id: 'hotel.nightaudit.pending', moduleId: 'hotel', capKey: 'nightaudit', railItemId: HOTEL_RAIL.audit, enabled: true, scope: 'site' },
      count: AUDIT.asReadonly(),
    },
  ];
}

/** Register the hotel's projections at the root (AppModule), so the shell can show them. */
export function provideHotelAttention(): Provider[] {
  return [{ provide: ATTENTION_PROJECTIONS, useValue: hotelAttentionProjections, multi: true }];
}
